import { NavType, UserType } from "@/types";
import { Carrot, ChartColumn, ChartLine, Heart, House, Package, Users } from "lucide-react";
import { ROUTES } from "./routes";

export const UI = {
  buttons: {
    tryAgain: "Try again",
    select: "Select",
    reset: "Reset",
    clear: "Clear",
  },
  links: {
    goBack: "Go Back",
  },
  icons: {
    ordersTotal: ChartColumn,
    avgProductsDelivered: Package,
    avgItemsShared: ChartLine,
    avgUsersPerOrder: Users,
  },
  errors: {
    somethingWentWrong: "Something went wrong!",
    unknownError: "Unknown error",
    missingEnvironments: "Missing environment variables",
    navError: (key: string) => `Navigation item with key "${key}" not found`,
    actionError: (key: string) => `[ActionsBlock] Action "${key}" has no Component`
  },
  success: {},
  info: {
    noUsersFound: "No users found!",
    noOrdersFound: "No orders found!",
    noProductsFound: "No products found!",
    noPreferencesFound: "No preferences found!",
    noItemsFound: "No items found!",
    noSessions: "No Sessions",
    noDelivery: "No Delivery",
    tryDifferentPeriod: "Try selecting a different period.",
    underDevelopment: "This page is currently under development.",
    searching: "Searching…",
    loading: "Loading…",
  },
  labels: {
    id: "ID",
    orderId: "Order ID",
    userId: "User ID",
    endWeek: "End Week",
    sessions: "Sessions",
    city: "City",
    zip: "ZIP",
    deliveriesTotal: "Deliveries",
    weeksTotal: "Weeks",
    productsChangesTotal: "Products Changes Total",
    totalSessions: "Total Sessions",
    totalEngagedTime: "Total Engaged Time (s)",
    productId: "Product ID",
    shareId: "Share ID",
    name: "Name",
    items: "Items",
    home: "Home",
    users: "Users",
    user: "User",
    orders: "Orders",
    details: "Details",
    orderDetails: "Order Details",
    orderPreferences: "Order Preferences",
    ordersCount: "Orders Count",
    deliveryDate: "Delivery Date",
    selectPeriod: "Select period",
    sharesTotal: "Shares Total",
    sharedProductsTotal: "Default Share items",
    productsAdded: "Products Added",
    productsRemoved: "Products Removed",
    productsTotal: "Products Delivered",
    percentChangeDeliveredItems: "Change Delivered",
    percentChangeAddedItems: "Change Added",
    percentChangeRemovedItems: "Change Removed",
    productName: "Product",
    products: "Products",
    shareName: "Share",
    usersTotal: "Users Total",
    count: "Count",
    price: "Price",
    preferences: "Preferences",
    delta: "Delta",
    type: "Type",
    productType: "Product Type",
    prefs: "Prefs",
    weeksDeliveries: "Weeks/Deliveries",
    userSkippedSomeWeeks: "User skipped some weeks",
    startWeek: "Weeks",
    week: "week",
    weekNumber: "Weeks",
    slash: "/",
    x: "x",
    s: "s",
    m: "m",
    h: "h",
  },
  options: {
    size: ["10", "25", "50", "100"],
  },
  placeholders: {
    search: "Search...",
  },
  titles: {
    goTo: "Go to",
    welcomeTo: "Welcome to the",
    flocktownDashboard: "Flocktown Dashboard",
    usersTop: (usersLength: number) => `Users Top ${usersLength}`,
    userProfile: (name: UserType["name"]) => `${name}'s Profile`,
    userOrdersTitle: (name: UserType["name"]) => `${name}'s Orders`,
    userPreferencesTitle: (name: UserType["name"]) => `${name}'s Preferences`,
    orderDetailsTitle: (name: UserType["name"]) => `${name}'s Order Details`,
    orderPreferencesTitle: (name: UserType["name"]) => `${name}'s Order Preferences`,
    productOrdersTitle: (productName: string) => `Orders for ${productName}`,
    products: "Products",
    orders: "Orders",
    preferences: "Preferences",
    shares: "Shares",
    defaultShareItems: "Default Share Items",
    deliveredItems: "Delivered Items",
    itemsAdded: "Items Added",
    itemsRemoved: "Items Removed",
    overview: "Overview",
    performanceChart: "Performance Chart",
    deliveryRatio: "Delivery Ratio",
    orderVolumeTrends: "Order Volume Trends",
    productPerformanceTrends: "Product Performance Trends",
    topProductsAdded: (amount: number) => `Top ${amount} Products Added`,
    topProductsRemoved: (amount: number) => `Top ${amount} Products Removed`,
    productOrdersChart: "Delivery Trends",
    productOrdersTable: "Order Details",
    ordersTotal: "Total Orders",
    avgProductsDelivered: "Avg Products Delivered",
    avgItemsShared: "Avg Share Items",
    avgUsersPerOrder: "Avg Users Per Order",
    dashboard: "Dashboard",
  },
  subtitles: {},
  descriptions: {
    mainLayout: "Flocktown Dashboard - Admin panel for Farmigo",
    periodFromTo: (from: string, to: string) => `Period from ${from} to ${to}`,
    vsPrevPeriod: (percent: number) => `${percent}% vs previous period`,
    noChange: "No change vs previous period",
  },
  footers: {
    total: "Total",
  },
  alts: {
    logo: "Flocktown Logo",
  },
  tooltips: {
    totallyAdded: "All items were added",
    totallyRemoved: "All items were removed",
    partiallyRemoved: "Some items were removed",
    partiallyAdded: "Some items were added",
    noChanges: "No changes",
    PI: "Purchased Items",
    CI: "Credit Items",
    products: {
      totallyAdded: "Products were added",
      totallyRemoved: "Products were removed",
      noChanges: "No changes in products",
    },
    exportUsers: "Export users data as CSV file.",
  },
  navigation: [
    {
      key: "home",
      label: "Home",
      href: ROUTES.home,
      Icon: House,
    },
    {
      key: "users",
      label: "Users",
      href: ROUTES.users.base,
      Icon: Users,
      subMenu: [
        {
          key: "preferences",
          label: "Preferences",
          href: ROUTES.users.preferences,
          Icon: Heart,
        },
      ],
    },
    {
      key: "products",
      label: "Products",
      href: ROUTES.products.base,
      Icon: Carrot,
    },
  ] as NavType[],
};

export type UIKey = keyof typeof UI;
